import { useState, useEffect, useCallback } from 'react';
import Navbar from '../components/Navbar';
import TopBar from '../components/TopBar';
import PostCard from '../components/PostCard';
import FilterChip from '../components/FilterChip';
import CrisisModal from '../components/CrisisModal';
import { tokens, TYPE_COLOR } from '../tokens';
import { useAuth } from '../context/AuthContext';
import useIsDesktop from '../useIsDesktop';
import api from '../api';

const typeOptions = ['Venting', 'Advice Needed', 'Win', 'Gratitude', 'Question'];
const filters = ['All', 'Venting', 'Advice Needed', 'Win', 'Gratitude', 'Question'];
const filterLabels = { 'Advice Needed': 'Advice' };

function FilterIcon({ type, size = 14 }) {
  const s = size;
  const stroke = 'currentColor';
  const sw = 1.4;
  const paths = {
    'All': <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round"><path d="M3 5 L17 5"/><path d="M3 10 L17 10"/><path d="M3 15 L17 15"/></g>,
    'Venting': <path d="M11 2 L5 11 L9 11 L8 18 L14 8 L10 8 L11 2 Z" fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round"/>,
    'Advice Needed': <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M10 3 L10 11"/><path d="M6 6 L6 13"/><path d="M14 6 L14 13"/><path d="M4 12 C4 15 7 17 10 17 C13 17 16 15 16 12"/></g>,
    'Win': <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M4 11 L8 15 L16 5"/></g>,
    'Gratitude': <path d="M10 16 C3 12 3 6 7 5 C9 5 10 7 10 7 C10 7 11 5 13 5 C17 6 17 12 10 16 Z" fill="none" stroke={stroke} strokeWidth={sw} strokeLinejoin="round"/>,
    'Question': <g fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"><path d="M6 7 C6 4 8 3 10 3 C12 3 14 4 14 6 C14 9 10 9 10 12"/><circle cx="10" cy="16" r="0.8" fill={stroke} stroke="none"/></g>,
  };
  return <svg width={s} height={s} viewBox="0 0 20 20" style={{ flexShrink: 0, display: 'block' }}>{paths[type] || paths['All']}</svg>;
}

export default function Stage() {
  const { user } = useAuth();
  const isDesktop = useIsDesktop();
  const [posts, setPosts] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [typeFilter, setTypeFilter] = useState('All');
  const [crisisPostIds, setCrisisPostIds] = useState(new Set());
  const [showCrisisModal, setShowCrisisModal] = useState(false);

  // Composer state
  const [composing, setComposing] = useState(false);
  const [composeContent, setComposeContent] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [composeError, setComposeError] = useState('');

  // Thesis modal state
  const [showThesis, setShowThesis] = useState(
    () => !localStorage.getItem('gl.stage.seenThesis')
  );

  const fetchPosts = useCallback(async (cursor = null, filter = typeFilter) => {
    const params = {};
    if (cursor) params.cursor = cursor;
    if (filter !== 'All') params.typeTag = filter;
    const { data } = await api.get('/posts', { params });
    return data;
  }, [typeFilter]);

  useEffect(() => {
    setLoading(true);
    fetchPosts(null, typeFilter)
      .then(data => { setPosts(data.posts); setNextCursor(data.nextCursor); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [typeFilter, fetchPosts]);

  const loadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const data = await fetchPosts(nextCursor);
      setPosts(prev => [...prev, ...data.posts]);
      setNextCursor(data.nextCursor);
    } catch {}
    setLoadingMore(false);
  };

  const handleCreated = (post, crisis) => {
    setPosts(prev => [post, ...prev]);
    if (crisis) {
      setCrisisPostIds(prev => new Set([...prev, post._id]));
      setShowCrisisModal(true);
    }
  };

  const handleDelete = (postId) => setPosts(prev => prev.filter(p => p._id !== postId));

  const handleSubmitPost = async () => {
    if (!composeContent.trim() || !selectedType) return;
    setComposeError('');
    setSubmitting(true);
    try {
      const roleTag = user?.department || 'General';
      const { data } = await api.post('/posts', {
        content: composeContent,
        typeTag: selectedType,
        roleTag,
      });
      handleCreated(data.post, data.crisis);
      setComposeContent('');
      setSelectedType('');
      setComposing(false);
    } catch (err) {
      setComposeError(err.response?.data?.error || 'Failed to create post');
    }
    setSubmitting(false);
  };

  const handleCancelCompose = () => {
    setComposeContent('');
    setSelectedType('');
    setComposeError('');
    setComposing(false);
  };

  const dismissThesis = () => {
    localStorage.setItem('gl.stage.seenThesis', '1');
    setShowThesis(false);
  };

  // Shared composer JSX
  const composerJSX = composing && (
    <div style={{
      padding: '14px 16px 14px',
      border: `1px solid ${tokens.accent}66`,
      background: tokens.accentGlow,
      animation: `gl-fade-up 300ms ${tokens.ease}`,
      marginBottom: 20,
    }}>
      {composeError && (
        <div style={{
          fontFamily: tokens.fontMono, fontSize: 11,
          letterSpacing: '0.06em', color: tokens.danger,
          marginBottom: 10, padding: '6px 0',
        }}>{composeError}</div>
      )}
      <textarea
        autoFocus
        value={composeContent}
        onChange={e => setComposeContent(e.target.value)}
        placeholder="What's on your mind?"
        maxLength={10000}
        style={{
          width: '100%', background: 'transparent', border: 'none', resize: 'none',
          color: tokens.textHi, fontFamily: tokens.fontDisplay, fontSize: 17,
          lineHeight: 1.6, minHeight: 80, outline: 'none', padding: '4px 0',
          boxSizing: 'border-box',
        }}
      />
      <div style={{
        fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.2em',
        textTransform: 'uppercase', color: tokens.textLow,
        marginTop: 10, marginBottom: 8,
      }}>Type &mdash; pick one</div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {typeOptions.map(t => {
          const selected = selectedType === t;
          const color = TYPE_COLOR[t] || tokens.accent;
          return (
            <button key={t} type="button" className="gl-btn-bare"
              onClick={() => setSelectedType(s => s === t ? '' : t)}
              style={{
                padding: '6px 11px',
                fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.14em',
                textTransform: 'uppercase',
                color: selected ? color : tokens.textLow,
                border: `1px solid ${selected ? color : tokens.edge}`,
                background: selected ? color + '1a' : 'transparent',
                transition: `all 200ms ${tokens.ease}`,
                cursor: 'pointer',
              }}
            >{t}</button>
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16, marginTop: 14 }}>
        <button type="button" className="gl-btn-bare" onClick={handleCancelCompose}
          style={{ fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: tokens.textLow }}>Cancel</button>
        <button type="button" className="gl-btn-bare"
          disabled={submitting || !composeContent.trim() || !selectedType}
          onClick={handleSubmitPost}
          style={{
            fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
            color: selectedType ? tokens.accent : tokens.textDim, opacity: selectedType ? 1 : 0.5,
            cursor: selectedType ? 'pointer' : 'not-allowed',
          }}>{submitting ? 'Posting...' : 'Post \u2192'}</button>
      </div>
    </div>
  );

  // Shared feed JSX
  const feedJSX = loading ? (
    <div style={{ padding: '36px 0' }}>
      <p style={{ fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase', color: tokens.textDim }}>Loading...</p>
    </div>
  ) : posts.length === 0 ? (
    <div style={{ padding: '32px 0' }}>
      <p style={{ fontFamily: tokens.fontDisplay, fontSize: 22, color: tokens.textMid, margin: '0 0 10px', fontWeight: tokens.displayWeight }}>The stage is quiet</p>
      <p style={{ fontFamily: tokens.fontBody, fontSize: 15, color: tokens.textLow, margin: 0, lineHeight: 1.6 }}>No posts yet. Be the first to step into the light.</p>
    </div>
  ) : (
    <>
      {posts.map(post => (
        <PostCard key={post._id} post={post} onDelete={handleDelete} showCrisis={crisisPostIds.has(post._id)} />
      ))}
      {nextCursor && (
        <div style={{ textAlign: 'center', padding: '28px 0' }}>
          <button className="gl-btn-bare" onClick={loadMore} disabled={loadingMore}
            style={{ fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
              color: tokens.accent, opacity: loadingMore ? 0.5 : 1, cursor: loadingMore ? 'not-allowed' : 'pointer' }}
          >{loadingMore ? 'Loading...' : 'Load more \u2192'}</button>
        </div>
      )}
      <div style={{ padding: '32px 0', textAlign: 'center' }}>
        <span style={{ fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.2em', color: tokens.textDim, textTransform: 'uppercase' }}>&middot; &middot; &middot;</span>
      </div>
    </>
  );

  /* ── Desktop: filter sidebar left, feed right ── */
  if (isDesktop) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column',
        background: tokens.ground, color: tokens.textHi, fontFamily: tokens.fontBody, position: 'relative' }}>
        <div className="gl-atmosphere" />
        <Navbar />

        <main style={{ flex: 1, overflowY: 'auto', position: 'relative', zIndex: 2 }}>
          <div style={{
            maxWidth: 1040, margin: '0 auto',
            display: 'grid', gridTemplateColumns: '200px 1fr',
            gap: 60, padding: '40px 40px 80px',
          }}>
            {/* Left rail — filters */}
            <aside style={{ position: 'sticky', top: 20, alignSelf: 'start' }}>
              <div style={{
                fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: tokens.textLow, marginBottom: 14,
              }}>Filter</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {filters.map(f => {
                  const active = typeFilter === f;
                  const color = TYPE_COLOR[f] || tokens.textHi;
                  return (
                    <button key={f} className="gl-btn-bare" onClick={() => setTypeFilter(f)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10,
                        padding: '8px 10px',
                        borderLeft: active ? `2px solid ${color}` : '2px solid transparent',
                        color: active ? color : tokens.textMid,
                        background: active ? color + '10' : 'transparent',
                        fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        transition: `all 180ms ${tokens.ease}`,
                      }}>
                      <FilterIcon type={f} size={14} />
                      {filterLabels[f] || f}
                    </button>
                  );
                })}
              </div>

              <div style={{ marginTop: 32 }}>
                <button className="gl-btn-bare" onClick={() => setComposing(c => !c)}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '8px 16px',
                    border: `1px solid ${tokens.accent}`,
                    borderRadius: 999,
                    color: composing ? tokens.ground : tokens.accent,
                    background: composing ? tokens.accent : tokens.accentGlow,
                    fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    transition: `all 200ms ${tokens.ease}`,
                  }}>
                  <span style={{ fontSize: 14, transform: composing ? 'rotate(45deg)' : 'none', display: 'inline-block', transition: `transform 200ms ${tokens.ease}` }}>+</span>
                  {composing ? 'Cancel' : 'Post'}
                </button>
              </div>
            </aside>

            {/* Feed column */}
            <div>
              {composerJSX}
              {feedJSX}
            </div>
          </div>
        </main>

        {showThesis && renderThesisModal()}
        {showCrisisModal && <CrisisModal onClose={() => setShowCrisisModal(false)} />}
      </div>
    );
  }

  /* ── Mobile: single column ── */
  return (
    <div style={{ position: 'relative', minHeight: '100vh', paddingBottom: 120 }}>
      <Navbar />
      <div className="gl-atmosphere" />

      <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <TopBar title="The Stage" amberTitle right={
        <button
          className="gl-btn-bare"
          onClick={() => setComposing(c => !c)}
          aria-label={composing ? 'Close composer' : 'Write a post'}
          style={{
            width: 34, height: 34, borderRadius: '50%',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            border: `1px solid ${tokens.accent}`,
            color: composing ? tokens.ground : tokens.accent,
            background: composing ? tokens.accent : tokens.accentGlow,
            fontSize: 20, lineHeight: 1, paddingBottom: 2,
            boxShadow: composing ? 'none' : `0 0 12px ${tokens.accent}55`,
            transition: `all 200ms ${tokens.ease}`,
            transform: composing ? 'rotate(45deg)' : 'none',
          }}
        >+</button>
      } />

      {/* Composer */}
      {composing && (
        <div style={{ padding: '16px 22px 0', position: 'relative', zIndex: 2 }}>
          {composerJSX}
        </div>
      )}

      {/* Filter chips */}
      <div style={{
        padding: '18px 18px 0', position: 'relative', zIndex: 2,
        display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-start',
      }}>
        {filters.map(f => (
          <FilterChip
            key={f}
            active={typeFilter === f}
            onClick={() => setTypeFilter(f)}
            tokens={tokens}
          >
            {filterLabels[f] || f}
          </FilterChip>
        ))}
      </div>

      {/* Post feed */}
      <div style={{ padding: '16px 22px 0', position: 'relative', zIndex: 2 }}>
        {feedJSX}
      </div>
      </div>{/* end max-width wrapper */}

      {showThesis && renderThesisModal()}
      {showCrisisModal && <CrisisModal onClose={() => setShowCrisisModal(false)} />}
    </div>
  );

  function renderThesisModal() {
    return (
      <div role="dialog" aria-modal="true" onClick={dismissThesis}
        style={{
          position: 'fixed', inset: 0, zIndex: 120,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 22px',
          background: 'rgba(15, 12, 10, 0.82)',
          backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
          animation: `gl-fade 320ms ${tokens.ease}`,
        }}>
        <div onClick={e => e.stopPropagation()}
          style={{
            maxWidth: 320, width: '100%', padding: '32px 28px 26px',
            border: `1px solid ${tokens.accent}55`, background: tokens.ground,
            boxShadow: `0 8px 48px rgba(0,0,0,0.6), 0 0 60px ${tokens.accent}1a`,
            animation: `gl-fade-up 420ms ${tokens.ease}`,
          }}>
          <div style={{ fontFamily: tokens.fontMono, fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: tokens.accent, marginBottom: 16 }}>The Stage</div>
          <p style={{ fontFamily: tokens.fontDisplay, fontStyle: 'italic', fontSize: 22, lineHeight: 1.35, color: tokens.textHi, margin: 0, textWrap: 'pretty' }}>
            Chronological.<br />
            <span style={{ color: tokens.textMid }}>No likes.</span><br />
            <span style={{ color: tokens.textMid }}>No algorithm.</span><br />
            <span style={{ color: tokens.textMid }}>Just people.</span>
          </p>
          <button className="gl-btn-bare" onClick={dismissThesis} style={{ marginTop: 28, fontFamily: tokens.fontMono, fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: tokens.accent }}>Enter &rarr;</button>
        </div>
      </div>
    );
  }
}
